import { useState, useEffect, useRef } from 'react'
import { X, ChevronDown, Check } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { cn } from '../../lib/utils'

const predefinedCategories = [
  'DeFi',
  'NFTs',
  'AI',
  'Infrastructure',
  'Gaming',
  'Social',
  'Privacy',
  'Scaling',
  'Interoperability',
  'DAO',
  'Metaverse',
  'Web3',
]

interface CategorySelectorProps {
  selectedCategories: string[]
  onCategoriesChange: (categories: string[]) => void
  maxCategories: number
}

export function CategorySelector({
  selectedCategories,
  onCategoriesChange,
  maxCategories,
}: CategorySelectorProps) {
  const [customCategory, setCustomCategory] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const toggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      onCategoriesChange(selectedCategories.filter((c) => c !== category))
    } else if (selectedCategories.length < maxCategories) {
      onCategoriesChange([...selectedCategories, category])
    }
  }

  const addCustomCategory = () => {
    if (
      customCategory.trim() &&
      !selectedCategories.includes(customCategory.trim()) &&
      selectedCategories.length < maxCategories
    ) {
      onCategoriesChange([...selectedCategories, customCategory.trim()])
      setCustomCategory('')
    }
  }

  const removeCategory = (category: string) => {
    onCategoriesChange(selectedCategories.filter((c) => c !== category))
  }

  const filteredCategories = predefinedCategories.filter((category) =>
    category.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const handleInputFocus = () => setIsOpen(true)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setIsOpen(true)
  }

  return (
    <div className="space-y-4">
      <div className="relative" ref={dropdownRef}>
        <div className="relative">
          <div className="min-h-[40px] w-full px-3 py-2 bg-white border border-neutral-200 rounded-md focus-within:border-neutral-800">
            <div className="flex flex-wrap gap-1 items-center">
              {selectedCategories.map((category) => (
                <div
                  key={category}
                  className="flex items-center gap-1 bg-brand-primary text-white px-2 py-1 rounded text-xs"
                >
                  <span>{category}</span>
                  <button
                    onClick={() => removeCategory(category)}
                    className="hover:bg-white/20 rounded-full p-0.5"
                    type="button"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <input
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                placeholder={
                  selectedCategories.length === 0 ? 'Search or select tags' : ''
                }
                className="flex-1 min-w-[120px] outline-none bg-transparent text-sm placeholder:text-neutral-400"
                disabled={selectedCategories.length >= maxCategories}
              />
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 rounded"
          >
            <ChevronDown
              className={cn(
                'w-4 h-4 text-neutral-500 transition-transform',
                isOpen && 'rotate-180',
              )}
            />
          </button>
        </div>
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white text-neutral-950 border border-neutral-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
            {filteredCategories.length > 0 ? (
              filteredCategories.map((category) => {
                const isSelected = selectedCategories.includes(category)
                const isDisabled =
                  !isSelected && selectedCategories.length >= maxCategories
                return (
                  <button
                    key={category}
                    type="button"
                    onClick={() => {
                      if (!isDisabled) toggleCategory(category)
                    }}
                    disabled={isDisabled}
                    className={cn(
                      'w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between',
                      isSelected && 'bg-brand-primary/5 text-brand-primary',
                      isDisabled && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    <span>{category}</span>
                    {isSelected && <Check className="w-4 h-4" />}
                  </button>
                )
              })
            ) : (
              <div className="px-3 py-2 text-sm text-neutral-500">
                No tags found
              </div>
            )}
          </div>
        )}
      </div>

      {selectedCategories.length < maxCategories && (
        <div className="space-y-1">
          <p className="text-sm font-normal">Add custom tags</p>
          <div className="flex gap-2 items-center">
            <Input
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              placeholder="Enter custom tag"
              onKeyPress={(e) => e.key === 'Enter' && addCustomCategory()}
              className="focus:border-neutral-800 focus-visible:border-neutral-800 my-0 placeholder:text-neutral-400"
            />
            <Button
              onClick={addCustomCategory}
              disabled={!customCategory.trim()}
              variant="outline"
              size="sm"
            >
              Add
            </Button>
          </div>
        </div>
      )}

      <p className="text-xs text-neutral-600">
        {selectedCategories.length}/{maxCategories} tags selected
      </p>
    </div>
  )
}
